const { identity, compose, inverse, applyToPoint, applyToPoints, translate, rotate, scale } = window.TransformationMatrix;

import { waitForImageLoad } from "./utils.js";

function applyMatrix(context, m)
{
    context.setTransform(m.a, m.b, m.c, m.d, m.e, m.f);
}

export class RemixDocument extends EventTarget
{
    constructor({
        width, height,
    })
    {
        super();

        this.width = width;
        this.height = height;
        this.layers = [];
        this._undoStack = [];
        this._redoStack = [];
    }

    get canUndo() { return this._undoStack.length > 0; }
    get canRedo() { return this._redoStack.length > 0; }

    clearUndo()
    {
        this._undoStack = [];
        this._redoStack = [];
        this.dispatchEvent(new Event("undochanged"));
    }

    pushUndo()
    {
        this._undoStack.push(this.serializeLayers());
        this._redoStack = [];
        this.dispatchEvent(new Event("undochanged"));
    }

    // Pop an undo state without applying it.
    popUndo()
    {
        this._undoStack.pop();
        this.dispatchEvent(new Event("undochanged"));
    }

    undo()
    {
        if(this._undoStack.length == 0)
            return;

        let state = this.serializeLayers();
        let undoState = this._undoStack.pop();
        this._applySerializedLayers(undoState);
        this._redoStack.push(state);
        this.fireRefresh();

        this.dispatchEvent(new Event("undochanged"));
    }

    redo()
    {
        if(this._redoStack.length == 0)
            return;

        let state = this.serializeLayers();
        let redoState = this._redoStack.pop();
        this._applySerializedLayers(redoState);
        this._undoStack.push(state);
        this.fireRefresh();

        this.dispatchEvent(new Event("undochanged"));
    }

    _applySerializedLayers(layersData)
    {
        this.layers = [];
        for(let layerData of layersData)
        {
            let layer = createFromSerializedLayer(layerData);
            this.addLayer(layer, { addUndo: false });
        }
    }

    serializeLayers()
    {
        let data = [];
        for(let layer of this.layers)
            data.push(layer.serialize());
        return data;
    }

    fireRefresh() { this.dispatchEvent(new Event("refresh")); }

    // Add a CanvasLayer.
    addLayer(layer, { addUndo=true } = {})
    {
        if(addUndo)
            this.pushUndo();

        layer.addEventListener("refresh", () => this.fireRefresh());
        this.layers.push(layer);
        this.fireRefresh();
    }

    removeLayer(layer)
    {
        let index = this.layers.indexOf(layer);
        if(index === -1)
            throw new Error("Layer not found");

        this.pushUndo();

        this.layers.splice(index, 1);
        this.fireRefresh();
    }

    moveLayerToTop(layer)
    {
        if(layer.fixed)
            return false;

        let index = this.layers.indexOf(layer);
        if(index === -1)
            throw new Error("Layer not found");

        this.layers.splice(index, 1);
        this.layers.push(layer);
        this.fireRefresh();
        return true;
    }

    intersectingLayer(x, y)
    {
        for(let i = this.layers.length - 1; i >= 0; i--)
        {
            let layer = this.layers[i];
            if(layer.hitTest(x, y))
                return layer;
        }

        return null;
    }

    get selectedLayer()
    {
        for(let layer of this.layers)
        {
            if(layer.selected)
                return layer;
        }
        return null;
    }

    set selectedLayer(layer=null)
    {
        for(let otherLayer of this.layers)
            otherLayer.selected = (otherLayer === layer);

        this.dispatchEvent(new Event("selectionchanged"));
    }

    draw({ canvas, context, drawClipBorder=false })
    {
        for(let layer of this.layers)
            layer.draw({ canvas, context, drawClipBorder });
    }

    async copyToClipboard()
    {
        // Clear the selection, so it doesn't draw into the output.
        let savedSelectedLayer = this.selectedLayer;
        this.selectedLayer = null;

        let canvas = document.createElement("canvas");
        canvas.width = this.width;
        canvas.height = this.height;
        let context = canvas.getContext("2d");

        let drawOptions = {
            canvas,
            context,
            drawClipBorder: true,
        };
        for(let layer of this.layers)
            layer.draw(drawOptions);

        this.selectedLayer = savedSelectedLayer;

        let blobPromise = new Promise((resolve) => {
            canvas.toBlob(resolve, "image/png");
        });

        let item = new ClipboardItem({ "image/png": blobPromise });
        await navigator.clipboard.write([item]);
    }
}

const ClassRegistry = new Map();

function registerLayerType(ctor)
{
    if(!ctor.type)
        throw new Error("Class must define a static 'type' string.");
    ClassRegistry.set(ctor.type, ctor);
    return ctor;
}

export function createFromSerializedLayer(state)
{
    let ctor = ClassRegistry.get(state.type);
    if(!ctor)
        throw new Error(`Unknown layer type: ${obj.type}`);
    return new ctor(state);
}

export class CanvasLayer extends EventTarget
{
    constructor({
        name,
        matrix = identity(),
        clipMatrix = identity(),
        fixed = false, // can't delete or change stacking order
        selected = false,
    })
    {
        super();
        this.name = name;
        this.matrix = matrix;
        this.clipMatrix = clipMatrix; // relative to matrix
        this.fixed = fixed;
        this._selected = selected;
    }

    draw({ canvas, context, drawClipBorder }) { throw new Error("Not implemented"); }
    get unscaledSize() { throw new Error("Not implemented"); }

    get selected() { return this._selected; }
    set selected(value) {
        this._selected = value;
        this.fireRefresh();
    }

    fireRefresh() { this.dispatchEvent(new Event("refresh")); }

    serialize()
    {
        return {
            type: this.constructor.type,
            name: this.name,
            matrix: this.matrix,
            clipMatrix: this.clipMatrix,
            fixed: this.fixed,
            selected: this.selected,
        };
    }

    clone()
    {
        let state = this.serialize();
        return createFromSerializedLayer(state);
    }

    hitTest(x, y)
    {
        let { width, height } = this.unscaledSize;
        let inv = inverse(this.matrix);
        let local = applyToPoint(inv, { x, y });
        return local.x >= 0 && local.y >= 0 && local.x <= width && local.y <= height;
    }

    // this.clipMatrix is relative to this.matrix.  Return the full composited matrix.
    get worldClipMatrix()
    {
        return compose(this.matrix, this.clipMatrix);
    }

    set worldClipMatrix(value)
    {
        this.clipMatrix = compose(inverse(this.matrix), value);
    }

    localCorners()
    {
        let { width, height } = this.unscaledSize;
        let p0 = { x: 0,       y: 0 };
        let p1 = { x: width,   y: 0 };
        let p2 = { x: width,   y: height };
        let p3 = { x: 0,       y: height };
        return [p0, p1, p2, p3];
    }

    corners({ forClip=false }={})
    {
        return applyToPoints(forClip? this.worldClipMatrix:this.matrix, this.localCorners());
    }

    get center()
    {
        let { width, height } = this.unscaledSize;
        return applyToPoint(this.matrix, { x: width / 2, y: height / 2 });
    }

    _drawSelectionBorder(context, { drawClipBorder })
    {
        if(!this.selected)
            return;
        
        this._drawSelectionBorderInner(context, {
            color: "#eAe0E2",
            dash: [6, 4],
            lineDashOffset: 0,
            drawClipBorder,
        });
        this._drawSelectionBorderInner(context, {
            color: "#2A2052",
            dash: [6, 4],
            lineDashOffset: 4,
            drawClipBorder,
        });
    }

    _drawSelectionBorderInner(context, {
        color = "#4A90E2",
        lineWidth = 2,
        dash = [6, 4],
        lineDashOffset = 0,
        drawClipBorder = false,
    } = {})
    {
        let [p0, p1, p2, p3] = this.corners({ forClip: drawClipBorder });

        context.save();
        try {
            // Identity transform => stroke width is in screen pixels
            // context.setTransform(1, 0, 0, 1, 0, 0);
            context.beginPath();
            context.moveTo(p0.x, p0.y);
            context.lineTo(p1.x, p1.y);
            context.lineTo(p2.x, p2.y);
            context.lineTo(p3.x, p3.y);
            context.closePath();

            context.strokeStyle = color;
            context.lineWidth = lineWidth;
            context.setLineDash(dash);
            context.lineDashOffset = lineDashOffset;
            context.stroke();
        } finally {
            context.restore();
        }
    }

    get translateX() { return this.matrix.e; }
    get translateY() { return this.matrix.f; }
    
    set translateX(value)
    {
        let dx = value - this.matrix.e;
        if (dx === 0) return;
        this.matrix = compose(translate(dx, 0), this.matrix);
        this.fireRefresh();
    }

    set translateY(value)
    {
        let dy = value - this.matrix.f;
        if (dy === 0) return;
        this.matrix = compose(translate(0, dy), this.matrix);
        this.fireRefresh();
    }

    _centerCanvasPoint()
    {
        let { width, height } = this.unscaledSize;
        return applyToPoint(this.matrix, { x: width / 2, y: height / 2 });
    }

    get zoom()
    {
        let { a, b } = this.matrix;
        return Math.hypot(a, b);
    }

    set zoom(targetScale)
    {
        let { a, b } = this.matrix;
        let currentScale = Math.hypot(a, b) || 1.0;
        if(targetScale <= 0)
            return;

        let factor = targetScale / currentScale;
        if(factor === 1)
            return;

        let p = this._centerCanvasPoint();
        this.matrix = compose(
            translate(p.x, p.y),
            scale(factor, factor),
            translate(-p.x, -p.y),
            this.matrix
        );
        this.fireRefresh();
    }

    // Rotation (degrees), around the object's current center
    get rotationDeg() {
        let { a, b } = this.matrix;
        let radians = Math.atan2(b, a);
        let degrees = radians * (180 / Math.PI);
        if (degrees >= 180) degrees -= 360;
        if (degrees < -180) degrees += 360;
        return degrees;
    }
    set rotationDeg(deg) {
        let currentRad = Math.atan2(this.matrix.b, this.matrix.a);
        let targetRad = (deg * Math.PI) / 180;
        let delta = targetRad - currentRad;
        if (delta === 0) return;

        let p = this._centerCanvasPoint();

        this.matrix = compose(
            translate(p.x, p.y),
            rotate(delta),
            translate(-p.x, -p.y),
            this.matrix
        );
        this.fireRefresh();
    }

}

export class CanvasLayerImage extends CanvasLayer
{
    static type="CanvasLayerImage";

    constructor({ src, width, height, ...rest })
    {
        super(rest);

        this.image = new Image();
        this.image.src = src;

        this.width = width;
        this.height = height;

        this._refreshOnLoad();
    }

    async _refreshOnLoad()
    {
        await waitForImageLoad(this.image);
        this.fireRefresh();
    }

    serialize()
    {
        return {
            ...super.serialize(),
            src: this.image.src,
            width: this.width,
            height: this.height,
        };
    }

    get unscaledSize()
    {
        return { width: this.width, height: this.height };
    }

    draw({ canvas, context, drawClipBorder })
    {
        let clipMatrix = compose(this.matrix, this.clipMatrix);

        context.save();
        try {
            const { width, height } = this.unscaledSize;

            // Apply the clipping matrix.
            applyMatrix(context, clipMatrix);
            context.beginPath();
            context.rect(0, 0, width, height);
            context.clip();
            context.closePath();

            // Draw the image.
            applyMatrix(context, this.matrix);
            context.drawImage(this.image, 0, 0, width, height);
        } finally {
            context.restore();
        }

        // Draw the selection border separately, so it isn't clipped by the mask.
        if(this.selected)
        {
            context.save();
            try {
                this._drawSelectionBorder(context, { drawClipBorder });
            } finally {
                context.restore();
            }
        }
    }
}
registerLayerType(CanvasLayerImage)
